import React, { useMemo } from 'react';
import { Card, CardContent, Typography, Box, Skeleton } from '@mui/material';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { format, parseISO, eachDayOfInterval, subDays } from 'date-fns';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const TYPE_COLORS = {
  SOLAR_FLARE:       { border: '#ffa726', bg: 'rgba(255,167,38,0.12)' },
  GEOMAGNETIC_STORM: { border: '#7e57c2', bg: 'rgba(126,87,194,0.12)' },
  CME:               { border: '#26c6da', bg: 'rgba(38,198,218,0.12)' },
};

export default function ActivityTrendChart({ dailyTrends, loading }) {
  const { labels, datasets } = useMemo(() => {
    // Build last-30-days label array
    const today = new Date();
    const days = eachDayOfInterval({ start: subDays(today, 29), end: today });
    const labels = days.map(d => format(d, 'MMM dd'));
    const labelKeys = days.map(d => format(d, 'yyyy-MM-dd'));

    // Group trend data
    const byTypeAndDate = {};
    (dailyTrends || []).forEach(row => {
      const key = row.type;
      if (!byTypeAndDate[key]) byTypeAndDate[key] = {};
      const dateStr = typeof row.date === 'string' ? row.date.slice(0, 10) : String(row.date);
      byTypeAndDate[key][dateStr] = (byTypeAndDate[key][dateStr] || 0) + Number(row.count);
    });

    const datasets = Object.entries(TYPE_COLORS).map(([type, color]) => ({
      label: type.replace(/_/g, ' '),
      data: labelKeys.map(dk => byTypeAndDate[type]?.[dk] || 0),
      borderColor: color.border,
      backgroundColor: color.bg,
      fill: true,
      tension: 0.4,
      pointRadius: 3,
      pointHoverRadius: 6,
      borderWidth: 2,
    }));

    return { labels, datasets };
  }, [dailyTrends]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        labels: { color: '#90caf9', font: { size: 11, weight: '600' }, boxWidth: 12, padding: 16 },
      },
      tooltip: {
        backgroundColor: 'rgba(10,22,40,0.95)',
        borderColor: 'rgba(255,255,255,0.3)', borderWidth: 1,
        titleColor: '#e3f2fd', bodyColor: '#90caf9',
        padding: 12, cornerRadius: 8,
      },
    },
    scales: {
      x: {
        ticks: { color: '#546e7a', font: { size: 10 }, maxTicksLimit: 10 },
        grid: { color: 'rgba(255,255,255,0.06)' },
      },
      y: {
        beginAtZero: true,
        ticks: { color: '#546e7a', font: { size: 11 }, stepSize: 1 },
        grid: { color: 'rgba(255,255,255,0.06)' },
      },
    },
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}>
          30-Day Activity Trends
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2, bgcolor: 'rgba(255,255,255,0.05)' }} />
        ) : (
          <Box sx={{ flex: 1, minHeight: 240, position: 'relative' }}>
            <Line data={{ labels, datasets }} options={options} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
